const cleanText = (value, max) => String(value || "").trim().slice(0, max);

export const buildTrainingObjective = (trainingPlan) => {
  const title = cleanText(trainingPlan?.title, 160);
  const details = cleanText(trainingPlan?.details, 900);
  return [title, details].filter(Boolean).join(": ");
};

export const createInitialTrainingExerciseState = (initialExercise = "") => ({
  exerciseId: 1,
  retryCount: 0,
  previousAttempt: "",
  currentExercise: cleanText(initialExercise, 1200),
  hasStarted: false,
});

export const buildTrainingRoleplayContext = (meeting) => ({
  meetingType: String(meeting?.meetingType || "practice"),
  userRole: String(meeting?.userRole || "participant"),
  speakingWith: String(meeting?.speakingWith || "partner"),
  behavior: String(meeting?.aiBehavior || "professional"),
  goals: [...(meeting?.goals || []), ...(meeting?.customGoals || [])].filter(Boolean).join("; "),
});

export const buildEvaluationToolResult = (
  args = {},
  currentExerciseState = createInitialTrainingExerciseState()
) => {
  const isRelevant = args?.isRelevant === true;
  const isSubstantive = args?.isSubstantive === true;
  const meetsObjective = args?.meetsObjective === true;
  const shouldAdvance =
    args?.shouldAdvance === true && isRelevant && isSubstantive && meetsObjective;
  const reason = String(args?.reason || "");
  const nextExerciseState = shouldAdvance
    ? {
        exerciseId: currentExerciseState.exerciseId + 1,
        retryCount: 0,
        previousAttempt: "",
        currentExercise: "",
        hasStarted: false,
      }
    : {
        ...currentExerciseState,
        retryCount: currentExerciseState.retryCount + 1,
      };

  return {
    output: {
      shouldAdvance,
      isRelevant,
      isSubstantive,
      meetsObjective,
      reason,
      exerciseState: nextExerciseState,
      instruction: shouldAdvance
        ? "Advance now to a fresh exercise with new context."
        : "Retry this exercise with one clear correction.",
    },
    nextExerciseState,
    shouldRequestNextScenario: shouldAdvance,
    usedRefinementThisTurn: !shouldAdvance,
    decisionReason: reason,
    transition: shouldAdvance ? "advance_exercise" : "increment_retry",
  };
};

export const buildScenarioToolOutput = (args = {}) => ({
  variationType: String(args?.variationType || "new_context"),
  scenario: String(args?.scenario || "Use a fresh realistic scenario tied to the training goal."),
  exampleLine: String(args?.exampleLine || "Create a new short practice line."),
  instruction:
    "Use this fresh scenario now. Do not reuse the previous sentence. Keep the transition natural.",
});

export const buildTrainingEvaluationRequest = (transcript, currentExerciseState) => {
  const trimmedTranscript = String(transcript || "").trim();
  const previousAttempt = String(currentExerciseState?.previousAttempt || "").trim();

  return {
    transcript: trimmedTranscript.slice(0, 240),
    previousAttempt: previousAttempt || "(none)",
    currentAttempt: trimmedTranscript,
    exerciseId: currentExerciseState?.exerciseId || 1,
    retryCount: currentExerciseState?.retryCount || 0,
    currentExercise: cleanText(currentExerciseState?.currentExercise, 1200),
    rule: "Advance only when the response is relevant, substantive, and meets the objective.",
  };
};
